import { useEffect, useRef, useState } from 'react'
import { Async } from '../async/Async'
import { Skeleton } from '../async/Skeleton'
import { Button } from '../primitives/Button'
import { SearchField } from '../primitives/SearchField'
import { useHitTarget } from '../responsive/hitTarget'
import { CatalogueIconGrid } from './CatalogueIconGrid'
import { IconGroupTabs } from './IconGroupTabs'
import {
  ICON_QUERY_MAX,
  useCatalogueIcons,
  useIconGroups,
  type CatalogueIcon,
} from './useIconCatalogue'
import './iconCatalogue.css'

/** How long a keystroke waits before it becomes a request — the same 300ms
 *  `MerchantsToolbar` uses. */
const DEBOUNCE_MS = 300

/**
 * The icon picker — #447. Search, group tabs, and a paged grid over the
 * backend's icon catalogue.
 *
 * Two callers since #461: the category form and the merchant form. Both write
 * paths validate `icon` against this one catalogue, so both pick from it here
 * rather than from a bundled set that would 422.
 *
 * **What it fetches, and what it never sends.** Groups from
 * `/category-icons/groups`; a blank search browses `/category-icons` for the
 * chosen group ("All" omits the parameter); a non-blank one searches
 * `/category-icons/search` across every group. `useCatalogueIcons` owns the
 * "never a blank `q`" rule, so it holds however this component's state ends
 * up.
 *
 * **The tabs scroll, with fades and Previous/Next** — `IconGroupTabs`, #455.
 *
 * **Search replaces the tabs while it is active.** It spans every group, so a
 * tab row that looked selectable but did nothing would be a lie. The tabs
 * return, on the group they were on, when the field is cleared.
 *
 * **Results scroll; pages accumulate.** Typo ranking is weak ("gitar" puts
 * guitar 10th), so "Show more" appends the next page inside the scrolling
 * box rather than replacing it. When it does, focus moves to the first new
 * icon: the button may be gone once the last page lands, and focus on a
 * removed node falls to `<body>`, out of the dialog's reading order.
 *
 * **"Show more" is `aria-disabled`, never native `disabled`, while a page
 * loads — issues 362/373's class of bug, again.** Disabling the FOCUSED
 * button moves focus to `<body>` in a real browser for the whole fetch, and
 * on a failed page nothing was there to bring it back — `focusFrom` only
 * fires on the count actually growing. Native `disabled` also happens to be
 * unnecessary here: `useIconCatalogue.ts`'s own `loadMore` already refuses a
 * second call while one is in flight, so the control can stay operable and
 * the guard is not lost by dropping the attribute.
 *
 * **Enter in the search field does not submit the form.** Implicit
 * submission would create the category mid-search.
 *
 * Both `<Async>`s are `surface="category-icons"` (live: no sync behind the
 * catalogue, so four states, and empty is a search with no match).
 */
export function IconCataloguePicker({
  value,
  onPick,
}: {
  /** The slug currently selected — the user's pick, or the stored icon. */
  value: string | null
  onPick: (icon: CatalogueIcon) => void
}) {
  const [draft, setDraft] = useState('')
  const [query, setQuery] = useState('')
  const [group, setGroup] = useState<string | null>(null)
  const chip = useHitTarget('form')

  useEffect(() => {
    if (draft === query) return
    const id = setTimeout(() => setQuery(draft), DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [draft, query])

  const searching = query.trim() !== ''
  const groups = useIconGroups()
  const { state, hasMore, loadingMore, loadMore } = useCatalogueIcons({ query, group })

  // See "Results scroll; pages accumulate" above.
  const results = useRef<HTMLDivElement>(null)
  const focusFrom = useRef<number | null>(null)
  const count = state.status === 'success' ? state.data.icons.length : 0
  useEffect(() => {
    const from = focusFrom.current
    if (from === null || count <= from) return
    focusFrom.current = null
    results.current?.querySelectorAll<HTMLInputElement>('input[type="radio"]')[from]?.focus()
  }, [count])

  return (
    <div
      className="icon-catalogue"
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.target instanceof HTMLInputElement && e.target.type === 'search') {
          e.preventDefault()
        }
      }}
    >
      <SearchField
        label="Search icons"
        placeholder="Search icons"
        value={draft}
        onChange={setDraft}
        maxLength={ICON_QUERY_MAX}
      />

      {searching ? (
        <p className="icon-catalogue__note">Results from every group.</p>
      ) : (
        <Async
          surface="category-icons"
          state={groups}
          skeleton={<Skeleton height={chip} />}
          empty={{ title: 'No icon groups' }}
        >
          {(list) => <IconGroupTabs groups={list} value={group} onChange={setGroup} />}
        </Async>
      )}

      <div className="icon-catalogue__results" ref={results}>
        <Async
          surface="category-icons"
          state={state}
          skeleton={
            <>
              <Skeleton height={44} />
              <Skeleton height={44} />
              <Skeleton height={44} />
            </>
          }
          empty={{
            title: searching ? `No icon matches “${query.trim()}”.` : 'No icons in this group.',
            // Search-only: clearing returns to browsing, which the browse-empty
            // case already IS — there is nothing narrower to undo there.
            action: searching
              ? {
                  label: 'Clear search',
                  onAct: () => {
                    setDraft('')
                    setQuery('')
                  },
                }
              : undefined,
          }}
        >
          {(data) => (
            <>
              <CatalogueIconGrid icons={data.icons} value={value} onChange={onPick} label="Icon" />
              {hasMore && (
                <Button
                  type="button"
                  variant="chip"
                  size="form"
                  aria-disabled={loadingMore || undefined}
                  onClick={() => {
                    focusFrom.current = data.icons.length
                    loadMore()
                  }}
                >
                  {loadingMore ? 'Loading…' : `Show more (${data.icons.length} of ${data.total})`}
                </Button>
              )}
            </>
          )}
        </Async>
      </div>
    </div>
  )
}
